/**
 * Shell MOBILE — la app en el gimnasio.
 *
 * Decisiones que vienen del contexto de uso y no del gusto:
 * - Navegación ABAJO, al alcance del pulgar. Una barra de tabs arriba obliga a recolocar la
 *   mano, y la otra mano puede estar sosteniendo una mancuerna.
 * - Cinco destinos frecuentes, más una puerta a los otros cuatro. Antes eran cinco y nada más, y
 *   Historial, Progreso, Foto y Ajustes quedaban al fondo del scroll de Inicio: si no scrolleabas
 *   hasta abajo, la app tenía cinco pantallas. Un sexto tab deja cada uno en 65 px de ancho
 *   (60 px en un Android de 360), que sigue siendo un objetivo cómodo porque lo que se acierta o
 *   se falla con el pulgar es el alto, y el alto son 56 px.
 * - Una sola columna, siempre. Nada que obligue a leer en zigzag con la vista cansada.
 */

import { useState, type ReactNode } from 'react';

import { PANTALLAS, useRuta, type Pantalla } from './router.tsx';
import { Icono, type NombreIcono } from './componentes.tsx';
import { IndicadorSync } from './IndicadorSync.tsx';
import { BotonAyuda } from './Ayuda.tsx';

/**
 * Los cinco de todos los días, elegidos por FRECUENCIA de uso y no por importancia conceptual.
 * Comida se registra 3–5 veces al día: es la acción más frecuente después de las series.
 *
 * El nombre de cada tab sale de `PANTALLAS` y no se escribe acá: así el tab, el título de la
 * cabecera y el encabezado de la ayuda no pueden separarse. Cuando podían, la pantalla central
 * era "Entrenar" en el tab y "Registrar" arriba, y la ayuda hablaba de una que no existía.
 */
const TABS: { pantalla: Pantalla; icono: NombreIcono }[] = [
  { pantalla: 'inicio', icono: 'casa' },
  { pantalla: 'registro', icono: 'mancuerna' },
  { pantalla: 'nutricion', icono: 'comida' },
  { pantalla: 'cuerpo', icono: 'balanza' },
  { pantalla: 'calendario', icono: 'calendario' },
];

/** Los de una vez por semana. Con nombre y con por qué: un ícono suelto no dice a qué se entra. */
const SECUNDARIOS: { pantalla: Pantalla; icono: NombreIcono; para: string }[] = [
  { pantalla: 'progreso', icono: 'progreso', para: 'Cómo viene cada ejercicio' },
  { pantalla: 'historial', icono: 'historial', para: 'Ver y corregir lo registrado' },
  { pantalla: 'ingesta', icono: 'camara', para: 'Leer una balanza o una máquina' },
  { pantalla: 'ajustes', icono: 'ajustes', para: 'Rutina, gimnasio, ayuda y datos' },
];

export function ShellMobile({ children }: { children: ReactNode }): ReactNode {
  const { pantalla, ir, detalle, volver } = useRuta();
  const [masAbierto, setMasAbierto] = useState(false);
  const enDetalle = detalle !== null;
  const enSecundaria = SECUNDARIOS.some((s) => s.pantalla === pantalla);

  return (
    <div className="m-app">
      <header className="m-cabecera">
        {enDetalle ? (
          <button type="button" className="boton boton--chico boton--fantasma" onClick={volver} aria-label="Volver">
            <Icono nombre="atras" tamano={18} />
          </button>
        ) : (
          <h1 className="titulo">{PANTALLAS[pantalla].titulo}</h1>
        )}

        {/* flex: none — los controles no se achican: el que cede el ancho en una pantalla angosta
            es el título, que se corta con puntos suspensivos. */}
        <div className="fila" style={{ gap: 4, flex: 'none' }}>
          <IndicadorSync compacto />
          <BotonAyuda variante="mobile" />
          <button
            type="button"
            className="boton boton--chico boton--fantasma"
            onClick={() => ir('ajustes')}
            aria-label="Ajustes"
          >
            <Icono nombre="ajustes" tamano={18} />
          </button>
        </div>
      </header>

      <main className="m-cuerpo">{children}</main>

      <nav className="m-tabs" aria-label="Secciones">
        {TABS.map((t) => (
          <button
            key={t.pantalla}
            type="button"
            className="m-tab"
            onClick={() => ir(t.pantalla)}
            {...(pantalla === t.pantalla ? { 'aria-current': 'page' as const } : {})}
          >
            <Icono nombre={t.icono} />
            {PANTALLAS[t.pantalla].titulo}
          </button>
        ))}

        <button
          type="button"
          className="m-tab"
          onClick={() => setMasAbierto(true)}
          aria-haspopup="dialog"
          {...(enSecundaria ? { 'aria-current': 'page' as const } : {})}
        >
          <Icono nombre="mas-destinos" />
          Más
        </button>
      </nav>

      {masAbierto && (
        <HojaInferior titulo="Más" onCerrar={() => setMasAbierto(false)}>
          <div className="columna" style={{ gap: 'var(--e2)' }}>
            {SECUNDARIOS.map((s) => (
              <button
                key={s.pantalla}
                type="button"
                className="boton"
                onClick={() => {
                  setMasAbierto(false);
                  ir(s.pantalla);
                }}
                style={{ justifyContent: 'flex-start', gap: 'var(--e3)', paddingLeft: 'var(--e4)' }}
              >
                <Icono nombre={s.icono} tamano={20} />
                <span className="columna" style={{ alignItems: 'flex-start', gap: 0 }}>
                  {PANTALLAS[s.pantalla].titulo}
                  <span className="nota--fina" style={{ fontWeight: 400 }}>
                    {s.para}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </HojaInferior>
      )}
    </div>
  );
}

/**
 * Hoja inferior. Es donde se registra una serie: sube desde abajo, así que los controles quedan
 * donde ya está el pulgar en vez de obligar a estirarse hasta el centro de la pantalla.
 */
export function HojaInferior({
  titulo,
  onCerrar,
  children,
}: {
  titulo: string;
  onCerrar: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <div className="m-hoja-fondo" onClick={onCerrar} aria-hidden="true" />
      <div className="m-hoja" role="dialog" aria-modal="true" aria-label={titulo}>
        <div className="fila fila--entre">
          <h2 className="titulo">{titulo}</h2>
          <button
            type="button"
            className="boton boton--chico boton--fantasma"
            onClick={onCerrar}
            aria-label="Cerrar"
          >
            <Icono nombre="cerrar" tamano={18} />
          </button>
        </div>
        {children}
      </div>
    </>
  );
}
